import { coalesceDescription } from './optimizationDescription';

const DEFAULT_FALLBACK_TEXT = 'Optimización sugerida';

const ensureWrappedInParagraph = (html, pClassName) => {
  const trimmed = String(html || '').trim();
  if (!trimmed) return '';
  const classAttr = pClassName ? ` class="${pClassName}"` : '';
  return `<p${classAttr}>${trimmed}</p>`;
};

export const sanitizeOptimizationDescription = (
  value,
  { fallbackText = DEFAULT_FALLBACK_TEXT, pClassName = '' } = {}
) => {
  const coalesced = coalesceDescription(value, fallbackText);
  const html = String(coalesced ?? '');

  // Allow only <p> and <b> tags, remove all attributes to avoid unsafe HTML.
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    const withoutBlocked = html
      .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '');

    const sanitized = withoutBlocked
      .replace(/<(?!\/?(p|b)\b)[^>]*>/gi, '')
      .replace(/<(?:(p|b))\b[^>]*>/gi, '<$1>');

    if (!pClassName) {
      return /<p\b/i.test(sanitized) ? sanitized : ensureWrappedInParagraph(sanitized, '');
    }

    const withClasses = sanitized.replace(/<p>/gi, `<p class="${pClassName}">`);
    return /<p\b/i.test(withClasses) ? withClasses : ensureWrappedInParagraph(withClasses, pClassName);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const allowedTags = new Set(['P', 'B']);
  const blockedTags = new Set(['SCRIPT', 'STYLE']);
  const toUnwrap = [];
  const toRemove = [];
  const walker = doc.createTreeWalker(doc.body, 1);

  while (walker.nextNode()) {
    const el = walker.currentNode;

    if (!allowedTags.has(el.tagName)) {
      if (blockedTags.has(el.tagName)) {
        toRemove.push(el);
      } else {
        toUnwrap.push(el);
      }
      continue;
    }

    for (const attr of Array.from(el.attributes)) {
      el.removeAttribute(attr.name);
    }

    if (el.tagName === 'P' && pClassName) {
      el.setAttribute('class', pClassName);
    }
  }

  for (const node of toRemove) {
    const parent = node.parentNode;
    if (parent) parent.removeChild(node);
  }

  for (const node of toUnwrap) {
    const parent = node.parentNode;
    if (!parent) continue;
    while (node.firstChild) {
      parent.insertBefore(node.firstChild, node);
    }
    parent.removeChild(node);
  }

  const hasParagraph = Boolean(doc.body.querySelector('p'));
  if (!hasParagraph) {
    const wrapped = ensureWrappedInParagraph(doc.body.innerHTML, pClassName);
    doc.body.innerHTML = wrapped;
  } else if (pClassName) {
    for (const p of Array.from(doc.body.querySelectorAll('p'))) {
      p.setAttribute('class', pClassName);
    }
  }

  return doc.body.innerHTML;
};
